import React, { useState, useEffect } from 'react';
import { Container, Typography, TextField, Button, CircularProgress, Alert, Paper, IconButton, InputAdornment } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import { showAlert } from '../components/service/AlertService';
import { resetPassword } from '../services/api/auth';

const ForgotPassword = () => {
  const { t, i18n } = useTranslation();
  const { actualTheme } = useTheme();

  const [token, setToken] = useState(new URLSearchParams(window.location.search).get('token') || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [hasToken, setHasToken] = useState(true);

  // Debug translation keys and current language
  useEffect(() => {
    console.log('Current language:', i18n.language);
    console.log('Translation for newPassword:', t('forgotPassword.newPassword'));
    console.log('Translation for confirmPassword:', t('forgotPassword.confirmPassword'));
    console.log('Translation for submitNewPassword:', t('forgotPassword.submitNewPassword'));
  }, [t, i18n.language]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
  }, []);

  useEffect(() => {
    if (!token) {
      setError(t('forgotPassword.invalidToken'));
      setHasToken(false);
    }
  }, [t, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    if (password !== confirmPassword) {
      setError(t('forgotPassword.passwordsDontMatch'));
      setLoading(false);
      return;
    }

    try {
      const response = await resetPassword(token, password);
      if (response.data) {
        setSuccess(true);
        showAlert('FORGOT_PASSWORD_COMPLETED', 'success');
      } else {
        throw new Error(response.error?.code || t('forgotPassword.error'));
      }
    } catch (err) {
      setError(err.message || t('forgotPassword.error'));
    } finally {
      setLoading(false);
    }
  };

  if (isLoggedIn) {
    return (
      <Container maxWidth="sm" sx={{ mt: 12, mb: 4 }}>
        <Paper
          sx={{
            p: 4,
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            borderRadius: 2,
            border: '1px solid rgba(255, 255, 255, 0.1)',
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" sx={{ color: actualTheme === 'dark' ? '#fff' : '#000' }}>
            {t('forgotPassword.alreadyLoggedIn')}
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 12, mb: 4 }}>
      <Paper
        sx={{
          p: 4,
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          border: '1px solid rgba(255, 255, 255, 0.1)',
          textAlign: 'center',
        }}
      >
        <Typography variant="h5" sx={{ mb: 3, color: actualTheme === 'dark' ? '#fff' : '#000' }}>
          {t('forgotPassword.title')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success ? (
          <Alert severity="success" sx={{ mb: 3 }}>
            {t('forgotPassword.success')}
          </Alert>
        ) : hasToken ? (
          <form onSubmit={handleSubmit}>
            <TextField
              label={t('forgotPassword.newPassword')}
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              fullWidth
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  background: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(0, 0, 0, 0.05)'
                      : 'rgba(255, 255, 255, 0.05)',
                  '& fieldset': {
                    borderColor: (theme) => theme.palette.divider,
                  },
                  '&:hover fieldset': {
                    borderColor: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.3)'
                        : 'rgba(255, 255, 255, 0.2)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'text.secondary',
                  '&.Mui-focused': {
                    color: 'primary.main',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  color: 'text.primary',
                },
              }}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: 'text.secondary' }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label={t('forgotPassword.confirmPassword')}
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              fullWidth
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  background: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(0, 0, 0, 0.05)'
                      : 'rgba(255, 255, 255, 0.05)',
                  '& fieldset': {
                    borderColor: (theme) => theme.palette.divider,
                  },
                  '&:hover fieldset': {
                    borderColor: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.3)'
                        : 'rgba(255, 255, 255, 0.2)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'text.secondary',
                  '&.Mui-focused': {
                    color: 'primary.main',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  color: 'text.primary',
                },
              }}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: 'text.secondary' }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading}
              sx={{
                textTransform: 'none',
                fontWeight: 400,
                background: (theme) =>
                  theme.palette.mode === 'light'
                    ? 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)'
                    : 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                boxShadow: '0 3px 5px 2px rgba(33, 203, 243, 0.3)',
                '&:hover': {
                  background: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'linear-gradient(45deg, #1976D2 30%, #00B4E5 90%)'
                      : 'linear-gradient(45deg, #1976D2 30%, #00B4E5 90%)',
                },
              }}
            >
              {loading ? <CircularProgress size={24} /> : t('forgotPassword.submit')}
            </Button>
          </form>
        ) : null}
      </Paper>
    </Container>
  );
};

export default ForgotPassword;